/**
 * @packageDocumentation
 * Implements the prize chart button and popup, listing available payouts and legend information for the current game.
 *
 * PrizeChartPopup (simplified to match Figma):
 * - No scrolling
 * - No carousel/multi-card generation
 * - Uses PC_Left and PC_Right exactly once
 * - Positions elements using Figma LTWH
 * - Uses portrait-specific layout + background texture when screen is portrait
 *
 * IMPORTANT: app.ts integration remains the same (PrizeChartPopup(app) and .visible toggling).
 */

import * as PIXI from "pixi.js";
import { Assets } from "pixi.js";
import { brightnessFilter } from "./global";
import { SoundManager } from "./sounds";

/**
 * Tracks which elements were visible before opening the popup (used to restore).
 */
let visibleElements: PIXI.ContainerChild[] = [];

/**
 * The button remains unchanged. It toggles the popup's visible state and hides other stage children.
 */
export class PrizeChartButton extends PIXI.Container {
  PrizeChartContainer: PIXI.Container;
  prizePopup!: PrizeChartPopup;

  constructor(app: PIXI.Application) {
    super();

    this.PrizeChartContainer = new PIXI.Container();

    const PrizeChartBg = new PIXI.Sprite(Assets.get("Prize_chart_but"));
    PrizeChartBg.anchor.set(0.5);
    PrizeChartBg.scale.set(0.75);

    this.PrizeChartContainer.addChild(PrizeChartBg);
    this.addChild(this.PrizeChartContainer);

    this.PrizeChartContainer.cursor = "pointer";
    this.PrizeChartContainer.interactive = true;

    this.PrizeChartContainer.on("pointertap", () => {
      SoundManager.Btnclick_1SndPlay();

      visibleElements = [];
      (app.stage.children as PIXI.ContainerChild[]).forEach((element) => {
        if (element.visible) visibleElements.push(element);
        element.visible = false;
      });

      this.prizePopup.visible = true;

      // Ensure popup draws above other things
      this.prizePopup.zIndex = 9999;
      (app.stage as any).sortableChildren = true;
    });

    this.PrizeChartContainer.on("pointerover", () => {
      this.PrizeChartContainer.filters = [brightnessFilter];
    });
    this.PrizeChartContainer.on("pointerout", () => {
      this.PrizeChartContainer.filters = [];
    });
    this.PrizeChartContainer.on("pointerdown", () => {
      this.PrizeChartContainer.scale.set(0.95);
    });
    this.PrizeChartContainer.on("pointerup", () => {
      this.PrizeChartContainer.scale.set(1);
    });
    this.PrizeChartContainer.on("pointerupoutside", () => {
      this.PrizeChartContainer.scale.set(1);
    });
  }
}

/**
 * Simplified PrizeChartPopup (matches Figma placement for both landscape and portrait).
 */
export class PrizeChartPopup extends PIXI.Container {
  private readonly app: PIXI.Application;

  // Overlay to block clicks behind the modal
  private overlay!: PIXI.Graphics;

  // Root container that we scale + position (design space -> screen space)
  private modalRoot!: PIXI.Container;

  // Elements
  private popupBg!: PIXI.Sprite;
  private pcLeft!: PIXI.Sprite;
  private pcRight!: PIXI.Sprite;

  private titleText!: PIXI.Text;
  private leftPayout1!: PIXI.Text;
  private leftPayout2!: PIXI.Text;
  private rightPayout!: PIXI.Text;
  private missionText!: PIXI.Text;

  private closeBtn!: PIXI.Sprite;

  private lastIsPortrait: boolean | null = null;

  // ----------------------------
  // LANDSCAPE (design space)
  // ----------------------------
  private static readonly L_DESIGN_W = 1920;
  private static readonly L_DESIGN_H = 1080;

  private static readonly L_MODAL = { left: 245, top: 121, w: 1456, h: 851 };
  private static readonly L_PC_LEFT = { left: 393, top: 351, w: 500, h: 311 };
  private static readonly L_PC_RIGHT = { left: 1084, top: 351, w: 500, h: 311 };

  private static readonly L_TEXT_TITLE = { w: 1074, top: 154 }; // centered
  private static readonly L_GROUP_103 = { left: 382, top: 685, w: 585, h: 87 };
  private static readonly L_TEXT_3BOARDERS = { left: 1041, top: 709, w: 585, h: 42 };
  private static readonly L_TEXT_REVEAL = { w: 988, h: 116, top: 850, centerOffsetX: 1 };
  private static readonly L_BTN_CLOSE = { left: 1631, top: 121, w: 70, h: 71 };

  // ----------------------------
  // PORTRAIT (design space)
  // Using a tall design space consistent with your portrait figma export.
  // ----------------------------
  private static readonly P_DESIGN_W = 1080;
  private static readonly P_DESIGN_H = 1920;

  private static readonly P_MODAL = { left: 84, top: 223, w: 870, h: 1627 };

  // match characters 1 (top cards) -> uses PC_Left
  private static readonly P_PC_LEFT = { left: 285, top: 587, w: 500, h: 311 };

  // 3card_matchingBoarders 1 (bottom cards) -> uses PC_Right
  private static readonly P_PC_RIGHT = { left: 284, top: 1069, w: 500, h: 311 };

  // title: centered with +18px, top 227, width 546
  private static readonly P_TEXT_TITLE = { w: 546, top: 227, centerOffsetX: 18 };

  // left payouts (two lines, centered) at specific tops
  private static readonly P_TEXT_LEFT1 = { left: 242, top: 921, w: 585, h: 42 };
  private static readonly P_TEXT_LEFT2 = { left: 242, top: 966, w: 585, h: 42 };

  // right payout purple
  private static readonly P_TEXT_3BOARDERS = { left: 241, top: 1392, w: 585, h: 42 };

  // mission bottom: centered with -19px, top 1585, width 546, height 209
  private static readonly P_TEXT_REVEAL = { w: 546, h: 209, top: 1585, centerOffsetX: -19 };

  // close button
  private static readonly P_BTN_CLOSE = { left: 901, top: 205, w: 70, h: 71 };

  constructor(app: PIXI.Application) {
    super();

    this.app = app;
    this.visible = false;
    this.interactive = true;
    this.sortableChildren = true;

    this.build();
    this.layout();

    window.addEventListener("resize", this.layout);
  }

  private build() {
    // Full-screen overlay (blocks clicks behind)
    this.overlay = new PIXI.Graphics();
    this.overlay.interactive = true;
    this.overlay.cursor = "default";
    this.addChild(this.overlay);

    // Modal root container
    this.modalRoot = new PIXI.Container();
    this.modalRoot.sortableChildren = true;
    this.addChild(this.modalRoot);

    // Background (texture will switch in layout depending on orientation)
    this.popupBg = new PIXI.Sprite(Assets.get("prizechart_popup"));
    this.popupBg.anchor.set(0, 0);
    this.modalRoot.addChild(this.popupBg);

    // Title
    this.titleText = new PIXI.Text({
      text: "Match characters or boarder to win big!!",
      style: {
        // Keep the same naming convention you already used elsewhere
        fontFamily: "RoadRage",
        fontStyle: "normal",
        fontWeight: "400",
        fontSize: 100,
        lineHeight: 110,
        fill: "#FFFFFF",
        align: "center",
        wordWrap: true,
        wordWrapWidth: 1074,
      },
    });
    this.titleText.anchor.set(0.5, 0);
    this.modalRoot.addChild(this.titleText);

    // PC assets (single instances)
    this.pcLeft = new PIXI.Sprite(Assets.get("PC_Left"));
    this.pcLeft.anchor.set(0, 0);
    this.modalRoot.addChild(this.pcLeft);

    this.pcRight = new PIXI.Sprite(Assets.get("PC_Right"));
    this.pcRight.anchor.set(0, 0);
    this.modalRoot.addChild(this.pcRight);

    // Left payout lines
    this.leftPayout1 = new PIXI.Text({
      text: "AT LEAST 1 CIVILIAN PAYS 10X YOUR BET!",
      style: {
        fontFamily: "PermanentMarker",
        fontStyle: "normal",
        fontWeight: "400",
        fontSize: 28,
        lineHeight: 41,
        fill: "#FFC21C",
        align: "center",
        wordWrap: true,
        wordWrapWidth: 585,
      },
    });
    this.leftPayout1.anchor.set(0.5, 0.5);
    this.modalRoot.addChild(this.leftPayout1);

    this.leftPayout2 = new PIXI.Text({
      text: "3 SUPER VILLAINS PAYS 100X YOUR BET!",
      style: {
        fontFamily: "PermanentMarker",
        fontStyle: "normal",
        fontWeight: "400",
        fontSize: 28,
        lineHeight: 41,
        fill: "#FF0000",
        align: "center",
        wordWrap: true,
        wordWrapWidth: 585,
      },
    });
    this.leftPayout2.anchor.set(0.5, 0.5);
    this.modalRoot.addChild(this.leftPayout2);

    // Right payout
    this.rightPayout = new PIXI.Text({
      text: "3 BOARDERS PAYS 10X YOUR BET!",
      style: {
        fontFamily: "PermanentMarker",
        fontStyle: "normal",
        fontWeight: "400",
        fontSize: 28,
        lineHeight: 41,
        align: "center",
        fill: "#A607FC",
        wordWrap: true,
        wordWrapWidth: 585,
      },
    });
    this.rightPayout.anchor.set(0.5, 0.5);
    this.modalRoot.addChild(this.rightPayout);

    // Mission text
    this.missionText = new PIXI.Text({
      text: "Reveal 3 Villains for a secret mission!",
      style: {
        fontFamily: "RoadRage",
        fontStyle: "normal",
        fontWeight: "400",
        fontSize: 100,
        lineHeight: 110,
        fill: "#FFFFFF",
        align: "center",
        wordWrap: true,
        wordWrapWidth: 988,
      },
    });
    this.missionText.anchor.set(0.5, 0.5);
    this.modalRoot.addChild(this.missionText);

    // Close button
    this.closeBtn = new PIXI.Sprite(Assets.get("close_btn"));
    this.closeBtn.anchor.set(0, 0);
    this.closeBtn.interactive = true;
    this.closeBtn.cursor = "pointer";

    // Hover affordance
    this.closeBtn.on("pointerover", () => {
      this.closeBtn.filters = [brightnessFilter];
    });
    this.closeBtn.on("pointerout", () => {
      this.closeBtn.filters = [];
    });

    this.closeBtn.on("pointertap", () => this.closePopup());
    this.modalRoot.addChild(this.closeBtn);
  }

  /**
   * Layout in design-space coordinates, then apply uniform scaling to match renderer size.
   */
  private layout = () => {
    const screenW = this.app.renderer.screen.width;
    const screenH = this.app.renderer.screen.height;

    // Overlay covers full renderer
    // ✅ Overlay must cover the FULL renderer even when stage is scaled/positioned by your resizer.
    const stage = this.app.stage as PIXI.Container;

    // Avoid divide-by-zero if something temporarily sets scale to 0
    const sx = stage.scale.x || 1;
    const sy = stage.scale.y || 1;

    // Cancel out stage transform so overlay renders in "screen space"
    this.overlay.scale.set(1 / sx, 1 / sy);
    this.overlay.position.set(-stage.position.x / sx, -stage.position.y / sy);

    const tl = this.toLocal(new PIXI.Point(0, 0), this.app.stage);
    const br = this.toLocal(new PIXI.Point(screenW, screenH), this.app.stage);

    this.overlay
      .clear()
      .beginFill(0x000000, 0.35)
      .drawRect(tl.x, tl.y, br.x - tl.x, br.y - tl.y)
      .endFill();

    // Ensure it blocks input over the full area too
    this.overlay.hitArea = new PIXI.Rectangle(tl.x, tl.y, br.x - tl.x, br.y - tl.y);


    const isPortrait = screenH > screenW;

    // Switch background texture only when orientation changes (avoids unnecessary texture assignments)
    if (this.lastIsPortrait !== isPortrait) {
      this.lastIsPortrait = isPortrait;
      this.popupBg.texture = isPortrait
        ? Assets.get("PC_modal_portrait")
        : Assets.get("prizechart_popup");
    }

    if (isPortrait) {
      this.layoutPortrait(screenW, screenH);
    } else {
      this.layoutLandscape(screenW, screenH);
    }
  };

  private layoutLandscape(screenW: number, screenH: number) {
    const s = Math.min(
      screenW / PrizeChartPopup.L_DESIGN_W,
      screenH / PrizeChartPopup.L_DESIGN_H
    );

    this.modalRoot.scale.set(s);

    const M = PrizeChartPopup.L_MODAL;

    // Modal root position
    this.modalRoot.position.set(M.left * s, M.top * s);

    // Background fits modal
    this.popupBg.position.set(0, 0);
    this.popupBg.width = M.w;
    this.popupBg.height = M.h;

    // Title centered (no offset), top 154
    const titleLeft = (PrizeChartPopup.L_DESIGN_W - PrizeChartPopup.L_TEXT_TITLE.w) / 2;
    this.titleText.style.wordWrapWidth = PrizeChartPopup.L_TEXT_TITLE.w;
    this.titleText.position.set(
      (titleLeft - M.left) + PrizeChartPopup.L_TEXT_TITLE.w / 2,
      PrizeChartPopup.L_TEXT_TITLE.top - M.top
    );

    // PC_Left / PC_Right
    const L = PrizeChartPopup.L_PC_LEFT;
    const R = PrizeChartPopup.L_PC_RIGHT;

    this.pcLeft.position.set(L.left - M.left, L.top - M.top);
    this.pcLeft.width = L.w;
    this.pcLeft.height = L.h;

    this.pcRight.position.set(R.left - M.left, R.top - M.top);
    this.pcRight.width = R.w;
    this.pcRight.height = R.h;

    // Group 103 (two-line payouts)
    const G = PrizeChartPopup.L_GROUP_103;
    const gx = (G.left - M.left) + G.w / 2;
    const gy = (G.top - M.top);

    this.leftPayout1.style.wordWrapWidth = G.w;
    this.leftPayout2.style.wordWrapWidth = G.w;

    this.leftPayout1.position.set(gx, gy + 22);
    this.leftPayout2.position.set(gx, gy + 22 + 42);

    // Right payout
    const T3 = PrizeChartPopup.L_TEXT_3BOARDERS;
    this.rightPayout.style.wordWrapWidth = T3.w;
    this.rightPayout.position.set(
      (T3.left - M.left) + T3.w / 2,
      (T3.top - M.top) + T3.h / 2
    );

    // Mission text (centered with +1px)
    const missionLeft =
      (PrizeChartPopup.L_DESIGN_W - PrizeChartPopup.L_TEXT_REVEAL.w) / 2 +
      PrizeChartPopup.L_TEXT_REVEAL.centerOffsetX;

    this.missionText.style.wordWrapWidth = PrizeChartPopup.L_TEXT_REVEAL.w;
    this.missionText.position.set(
      (missionLeft - M.left) + PrizeChartPopup.L_TEXT_REVEAL.w / 2,
      (PrizeChartPopup.L_TEXT_REVEAL.top - M.top) + PrizeChartPopup.L_TEXT_REVEAL.h / 2
    );

    // Close button
    const C = PrizeChartPopup.L_BTN_CLOSE;
    this.closeBtn.position.set(C.left - M.left, C.top - M.top);
    this.closeBtn.width = C.w;
    this.closeBtn.height = C.h;
  }

  private layoutPortrait(screenW: number, screenH: number) {
    const s = Math.min(
      screenW / PrizeChartPopup.P_DESIGN_W,
      screenH / PrizeChartPopup.P_DESIGN_H
    );

    this.modalRoot.scale.set(s);

    const M = PrizeChartPopup.P_MODAL;

    // Modal root position
    this.modalRoot.position.set(M.left * s, M.top * s);

    // Background fits modal
    this.popupBg.position.set(0, 0);
    this.popupBg.width = M.w;
    this.popupBg.height = M.h;

    // Title: centered with +18px, top 227, width 546
    const titleCenterX =
      PrizeChartPopup.P_DESIGN_W / 2 + PrizeChartPopup.P_TEXT_TITLE.centerOffsetX;

    this.titleText.style.wordWrapWidth = PrizeChartPopup.P_TEXT_TITLE.w;
    this.titleText.position.set(
      (titleCenterX - M.left), // modal-local center
      PrizeChartPopup.P_TEXT_TITLE.top - M.top
    );

    // PC_Left (top cards)
    const L = PrizeChartPopup.P_PC_LEFT;
    this.pcLeft.position.set(L.left - M.left, L.top - M.top);
    this.pcLeft.width = L.w;
    this.pcLeft.height = L.h;

    // PC_Right (bottom cards)
    const R = PrizeChartPopup.P_PC_RIGHT;
    this.pcRight.position.set(R.left - M.left, R.top - M.top);
    this.pcRight.width = R.w;
    this.pcRight.height = R.h;

    // Left payout line 1 (At least 1 CIVILIAN...)
    const P1 = PrizeChartPopup.P_TEXT_LEFT1;
    this.leftPayout1.style.wordWrapWidth = P1.w;
    this.leftPayout1.position.set(
      (P1.left - M.left) + P1.w / 2,
      (P1.top - M.top) + P1.h / 2
    );

    // Left payout line 2 (3 SUPER VILLAINS...)
    const P2 = PrizeChartPopup.P_TEXT_LEFT2;
    this.leftPayout2.style.wordWrapWidth = P2.w;
    this.leftPayout2.position.set(
      (P2.left - M.left) + P2.w / 2,
      (P2.top - M.top) + P2.h / 2
    );

    // Right payout (purple)
    const T3 = PrizeChartPopup.P_TEXT_3BOARDERS;
    this.rightPayout.style.wordWrapWidth = T3.w;
    this.rightPayout.position.set(
      (T3.left - M.left) + T3.w / 2,
      (T3.top - M.top) + T3.h / 2
    );

    // Mission text: centered with -19px, top 1585, width 546, height 209
    const missionCenterX =
      PrizeChartPopup.P_DESIGN_W / 2 + PrizeChartPopup.P_TEXT_REVEAL.centerOffsetX;

    this.missionText.style.wordWrapWidth = PrizeChartPopup.P_TEXT_REVEAL.w;
    this.missionText.position.set(
      (missionCenterX - M.left),
      (PrizeChartPopup.P_TEXT_REVEAL.top - M.top) + PrizeChartPopup.P_TEXT_REVEAL.h / 2
    );

    // Close button
    const C = PrizeChartPopup.P_BTN_CLOSE;
    this.closeBtn.position.set(C.left - M.left, C.top - M.top);
    this.closeBtn.width = C.w;
    this.closeBtn.height = C.h;
  }

  /**
   * Restore stage and hide popup.
   * This preserves the existing app.ts integration behavior.
   */
  closePopup() {
    SoundManager.Btnclick_1SndPlay();
    visibleElements.forEach((element) => (element.visible = true));
    this.visible = false;
  }
}
